import { Link } from "react-router-dom";
import { articles, categories, editorPicks } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

function HeroSection() {
  const featured = articles.find((a) => a.featured);
  if (!featured) return null;

  return (
    <section className="relative h-[500px] md:h-[700px] w-full overflow-hidden">
      <img
        src={featured.image}
        alt={featured.title}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
        <div className="max-w-4xl">
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full mb-6">
            {featured.categoryName}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
            {featured.title}
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
            {featured.excerpt}
          </p>
          <Link
            to={`/article/${featured.id}`}
            className="inline-flex items-center gap-2 bg-white text-foreground-950 px-6 py-3 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            阅读全文
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featuredArticles = articles.filter((a) => a.featured).slice(0, 2);
  const mainArticle = featuredArticles[0];
  const subArticle = featuredArticles[1];

  if (!mainArticle) return null;

  return (
    <section className="px-4 md:px-8 py-12 md:py-20">
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
        <div className="lg:w-1/2">
          <div className="relative rounded-2xl overflow-hidden group">
            <img
              src={mainArticle.image}
              alt={mainArticle.title}
              className="w-full h-[350px] md:h-[500px] object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute top-4 left-4 flex gap-2">
              <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full">
                独家
              </span>
              <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full">
                深度
              </span>
            </div>
          </div>
        </div>

        <div className="lg:w-1/2 flex flex-col gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="border border-primary-500 text-primary-500 text-xs font-medium px-3 py-1 rounded-full">
                编辑精选
              </span>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            <Link to={`/article/${mainArticle.id}`}>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight hover:text-primary-600 transition-colors">
                {mainArticle.title}
              </h2>
            </Link>
            <p className="text-foreground-600 mt-3 text-sm md:text-base leading-relaxed">
              {mainArticle.excerpt}
            </p>
            <div className="flex items-center gap-3 mt-4">
              <img
                src={mainArticle.authorAvatar}
                alt={mainArticle.author}
                className="w-8 h-8 rounded-full object-cover"
              />
              <span className="text-sm text-foreground-600">{mainArticle.author}</span>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-500">{mainArticle.date}</span>
            </div>
          </div>

          {subArticle && (
            <div className="bg-foreground-950 rounded-2xl p-5 md:p-6 mt-auto">
              <span className="text-xs text-accent-400 font-medium mb-2 block">
                深度报道
              </span>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Link to={`/article/${subArticle.id}`}>
                    <h3 className="font-heading text-lg md:text-xl font-bold text-white leading-snug hover:text-accent-400 transition-colors">
                      {subArticle.title}
                    </h3>
                  </Link>
                  <p className="text-sm text-foreground-400 mt-2 line-clamp-2">
                    {subArticle.excerpt}
                  </p>
                </div>
                <img
                  src={subArticle.image}
                  alt={subArticle.title}
                  className="w-full sm:w-32 h-24 sm:h-28 object-cover rounded-lg flex-shrink-0"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function CategoriesGrid() {
  const catArticles = categories.map((cat) => ({
    ...cat,
    article: articles.find((a) => a.category === cat.id),
  }));

  return (
    <section className="px-4 md:px-8 py-12 md:py-20 bg-background-100">
      <div className="text-center mb-10">
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
          探索<span className="text-accent-500">热门</span>分类
        </h2>
        <p className="text-foreground-500 mt-2 text-sm md:text-base">
          发现不同领域的<span className="text-accent-500">精彩内容</span>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Link
          to={`/category/${catArticles[0].slug}`}
          className="relative rounded-2xl overflow-hidden group md:row-span-2 h-[300px] md:h-auto min-h-[300px]"
        >
          <img
            src={catArticles[0].article?.image || ""}
            alt={catArticles[0].name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full mb-2 inline-block">
              {catArticles[0].articleCount} 篇文章
            </span>
            <h3 className="font-heading text-xl md:text-2xl font-bold text-white">
              {catArticles[0].name}
            </h3>
            <p className="text-sm text-white/70 mt-1">{catArticles[0].description}</p>
          </div>
        </Link>

        <Link
          to={`/category/${catArticles[1].slug}`}
          className="relative rounded-2xl overflow-hidden group h-[200px] md:h-[240px]"
        >
          <img
            src={catArticles[1].article?.image || ""}
            alt={catArticles[1].name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="font-heading text-lg md:text-xl font-bold text-white">
              {catArticles[1].name}
            </h3>
            <p className="text-sm text-white/70">{catArticles[1].description}</p>
          </div>
        </Link>

        <Link
          to={`/category/${catArticles[2].slug}`}
          className="relative rounded-2xl overflow-hidden group h-[200px] md:h-[240px]"
        >
          <img
            src={catArticles[2].article?.image || ""}
            alt={catArticles[2].name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="font-heading text-lg md:text-xl font-bold text-white">
              {catArticles[2].name}
            </h3>
            <p className="text-sm text-white/70">{catArticles[2].description}</p>
          </div>
        </Link>
      </div>
    </section>
  );
}

function LatestNews() {
  const latestArticles = articles.slice(0, 6);

  return (
    <section className="px-4 md:px-8 py-12 md:py-20">
      <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 mb-2">
        最新资讯
      </h2>
      <p className="text-foreground-500 text-sm md:text-base mb-10">
        每日精选，深度解读
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {latestArticles.map((article) => (
          <Link
            key={article.id}
            to={`/article/${article.id}`}
            className="group block border border-background-200/70 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="relative h-48 md:h-56 overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-4 md:p-5">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                {article.title}
              </h3>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-primary-500 font-medium">
                  {article.categoryName}
                </span>
                <span className="text-foreground-300">·</span>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="text-center mt-10">
        <Link
          to="/category/technology"
          className="inline-flex items-center gap-2 border border-foreground-300 text-foreground-700 px-5 py-2.5 rounded-full text-sm font-medium hover:bg-foreground-950 hover:text-background-50 transition-colors whitespace-nowrap"
        >
          查看更多文章
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line" />
          </span>
        </Link>
      </div>
    </section>
  );
}

function EditorPick() {
  const pick = editorPicks[0];
  if (!pick) return null;

  return (
    <section className="px-4 md:px-8 py-12 md:py-20">
      <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
        <div className="lg:w-3/5">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-2 h-2 bg-foreground-950 rounded-full" />
            <span className="text-sm font-medium text-foreground-700">主编推荐</span>
          </div>
          <blockquote className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-relaxed">
            <span className="text-primary-500">"</span>
            {pick.quote}
            <span className="text-primary-500">"</span>
          </blockquote>
          <div className="flex items-center gap-3 mt-6">
            <span className="font-body font-semibold text-foreground-950">
              {pick.author}
            </span>
            {pick.verified && (
              <span className="flex items-center gap-1 text-xs text-accent-500">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-verified-badge-fill" />
                </span>
                {pick.title}
              </span>
            )}
          </div>
        </div>
        <div className="lg:w-2/5">
          <img
            src={pick.avatar}
            alt={pick.author}
            className="w-full h-[400px] md:h-[500px] object-cover object-top rounded-2xl"
          />
        </div>
      </div>
    </section>
  );
}

function SubscribeCTA() {
  return (
    <section className="relative h-[400px] md:h-[500px] w-full overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Morning%20newsroom%20editorial%20scene%20with%20warm%20sunlight%20streaming%20through%20large%20windows%2C%20desks%20with%20laptops%20and%20papers%2C%20coffee%20cups%2C%20newspapers%20scattered%2C%20cozy%20productive%20atmosphere%2C%20editorial%20photography%20style%2C%20warm%20golden%20tones%2C%20soft%20focus%20background&width=1600&height=900&seq=cta-bg&orientation=landscape"
        alt="Subscribe"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-16">
        <div className="max-w-lg">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
            订阅我们的早报
          </h2>
        </div>
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <Link
            to="/about"
            className="inline-flex items-center gap-3 bg-white text-foreground-950 px-5 py-3 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            立即订阅
            <span className="w-8 h-8 bg-foreground-950 rounded-full flex items-center justify-center">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-white text-xs" />
              </span>
            </span>
          </Link>
          <p className="text-sm md:text-base text-white/80 max-w-xs leading-relaxed">
            每周一至周五，精选全球最重要的新闻与深度分析，让您的早晨更有价值。
          </p>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "http://dlqlbdnn.cn/",
        "url": "http://dlqlbdnn.cn/",
        "name": "洞察日报 - 深度内容资讯平台",
        "description": "洞察日报致力于为读者提供科技、商业、生活、文化和国际领域的深度内容。",
        "publisher": {
          "@type": "Organization",
          "name": "洞察日报",
          "logo": {
            "@type": "ImageObject",
            "url": "http://dlqlbdnn.cn/logo.png"
          }
        }
      },
      {
        "@type": "Organization",
        "name": "洞察日报",
        "url": "http://dlqlbdnn.cn/",
        "logo": "http://dlqlbdnn.cn/logo.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "hello@dlqlbdnn.cn",
          "contactType": "business"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main>
        <div className="pt-0">
          <HeroSection />
        </div>
        <FeaturedSection />
        <CategoriesGrid />
        <LatestNews />
        <EditorPick />
        <SubscribeCTA />
      </main>
      <Footer />
    </div>
  );
}
